import React from 'react';
import {
  FormControl,
  TextField,
  MenuItem,
  InputLabel,
  Select,
  Icon,
  Chip,
  Checkbox,
  ListItemText,
  LinearProgress,
  Tooltip,
  ListSubheader,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { getBarValue, USER_CLASSIFICATION } from '../../../utility/common';
import { ArrowDownward, Star } from '@material-ui/icons';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';

import Signal from '../../shared/Signal';

/** Below this many options the list fits on screen, so a search box would be noise. */
export const SEARCHABLE_MIN_OPTIONS = 8;

export default function FormDropDown(props) {
  const {
    className,
    id,
    onChange,
    defaultValue,
    options,
    withCheckBoxes,
    fullHeight,
    hierarchicalSelect,
    multiple,
    value,
    fullWidth,
    placeholder,
    style,
    label,
    multipleOption,
    disabled,
    isForKeywordDropdown,
    isTopicsDropDown,
    downArrowChange = '',
    classes = '',
    isAtBottom = false,
    sessionUserType = '',
    classNameForMenuItem = '',
    displayGroupName = false,
    groupNameOptionClassName = '',
    searchable = false,
    searchPlaceholder = 'Search',
    noSearchResultsText = 'No matches found',
  } = props;
  const [open, toggleopen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');

  // Opt-in only: without `searchable` every existing dropdown keeps rendering exactly as
  // before. The box is withheld on short lists, where the whole list is visible anyway.
  const showSearchBox = searchable && (options?.length || 0) >= SEARCHABLE_MIN_OPTIONS;
  const searchNeedle = searchQuery.trim().toLowerCase();
  const matchesSearch = React.useCallback(
    (option) =>
      !showSearchBox ||
      !searchNeedle ||
      String(option?.text ?? '')
        .toLowerCase()
        .includes(searchNeedle),
    [showSearchBox, searchNeedle]
  );
  // Non-matching items are hidden rather than unmounted: dropping the selected MenuItem
  // makes MUI lose the value it renders in the closed input.
  const hasSearchMatch = !showSearchBox || (options || []).some((option) => !option?.hidden && matchesSearch(option));
  // Select clones EVERY child with its own onClick, which closes the menu and reports that
  // child's value as the new selection. For the rows that are not real options — the search
  // box and the empty-state message — that value is undefined, so an unguarded click would
  // wipe the current selection. Their contents are wrapped in this guard. Escape is let
  // through on purpose so it still closes the menu.
  const stopMenuEvents = {
    onClick: (event) => event.stopPropagation(),
    onMouseDown: (event) => event.stopPropagation(),
    onKeyUp: (event) => event.stopPropagation(),
    onKeyDown: (event) => {
      if (event.key !== 'Escape') {
        event.stopPropagation();
      }
    },
  };
  return hierarchicalSelect ? (
    <Autocomplete
      disableClearable
      classes={{
        inputRoot: 'inputRoot-override',
      }}
      options={options}
      defaultValue={value}
      autoComplete={true}
      onOpen={() => toggleopen(true)}
      onClose={() => toggleopen(!open)}
      open={open}
      groupBy={(option) => option.groupBy}
      getOptionLabel={(option) => option.label}
      getOptionSelected={(option) => option.value}
      onChange={(e, res) => {
        onChange(res);
        toggleopen(!open);
      }}
      renderInput={(params) => (
        <TextField
          id="content_recommendation__textfield__form_drop_down_0"
          disabled={true}
          {...params}
          inputProps={{
            ...params.inputProps,
            readOnly: true,
            className: 'cursor-pointer',
            onClick: () => toggleopen(true),
          }}
          value={params.value || value}
          label={params.label || props.label || options[0].label}
          variant="outlined"
          fullWidth
        />
      )}
    />
  ) : multiple ? (
    <FormControl id="content_recommendation__formcontrol__form_drop_down_1" variant="outlined">
      <InputLabel htmlFor="outlined-age-native-simple">Just a Demo</InputLabel>
      <Select
        multiple
        value={['Ten', 'Twenty']}
        style={{ width: '300px', borderRadius: '0px' }}
        label="Just a Demo"
        IconComponent={() => <Icon style={{ display: 'none' }} />}
        MenuProps={{
          getContentAnchorEl: null,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
        }}
        inputProps={{
          name: 'demo',
          id: 'outlined-age-native-simple',
        }}
        renderValue={(selected) => (
          <div>
            {selected.map((value) => (
              <Chip key={value} label={value} />
            ))}
          </div>
        )}
      >
        <MenuItem value="Ten">Ten</MenuItem>
        <MenuItem value="Twenty">Twenty</MenuItem>
        <MenuItem value="Thirty">Thirty</MenuItem>
      </Select>
    </FormControl>
  ) : withCheckBoxes ? (
    <FormControl
      id="content_recommendation__formcontrol__form_drop_down_2"
      variant="outlined"
      className={className || `simple-dropdown`}
    >
      <InputLabel id="demo-mutiple-checkbox-label">{label}</InputLabel>
      <Select
        className={`simple-dropdown ${fullWidth ? 'w-100' : ''}`}
        multiple
        value={['Name']}
        placeholder="asdfd"
        MenuProps={{
          getContentAnchorEl: null,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
        }}
      >
        {options.map((i) => (
          <MenuItem key={i.value} value={['Name']}>
            <Checkbox />
            <ListItemText primary={i.text} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  ) : multipleOption ? (
    <Autocomplete
      classes={{
        inputRoot: 'inputRoot-override',
        listbox: 'autocomplete-listbox-wrapper',
      }}
      disableClearable
      options={options}
      defaultValue={value}
      onChange={(e, res) => {
        onChange(res);
      }}
      getOptionLabel={(option) =>
        multiple ? (
          <span>
            {option.filterType ? <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>{option.filterType}: </span> : ''}
            {option.text}
          </span>
        ) : (
          option.text || ''
        )
      }
      value={value}
      style={{ width: fullWidth ? '100%' : 300 }}
      renderOption={(option) => {
        return (
          <div id="content_recommendation__div__form_drop_down_3" className={'normal'}>
            {option.text}
          </div>
        );
      }}
      renderInput={(params) => (
        <TextField
          id="content_recommendation__textfield__form_drop_down_4"
          disabled={true}
          {...params}
          inputProps={{
            ...params.inputProps,
            readOnly: true,
            onClick: () => toggleopen(true),
          }}
          value={params.value || value}
          label={params.label || props.label || options[0].label}
          variant="outlined"
          fullWidth
        />
      )}
    />
  ) : (
    <FormControl
      id="content_recommendation__formcontrol__form_drop_down_5"
      variant="outlined"
      className={className || `simple-dropdown`}
      disabled={disabled}
    >
      {label ? <InputLabel id={label}>{label}</InputLabel> : null}
      <Select
        id={id}
        label={label}
        style={style}
        labelId={label}
        placeholder={placeholder}
        className={`simple-dropdown ${downArrowChange ? downArrowChange : 'down-arrow'} ${fullWidth ? 'w-100' : ''} ${
          fullHeight ? 'h-100' : ''
        }`}
        onChange={onChange}
        defaultValue={defaultValue}
        onClose={showSearchBox ? () => setSearchQuery('') : undefined}
        MenuProps={{
          className: `dropdown-popover-wrapper ${isAtBottom ? `bottom-dropdown-styles` : ''} ${classNameForMenuItem}`,
          getContentAnchorEl: null,
          // Without this the menu focuses the selected option on open and the search box
          // never receives the first keystroke.
          ...(showSearchBox ? { autoFocus: false } : {}),
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          ...(isAtBottom
            ? {
                transformOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
              }
            : {}),
        }}
        value={value}
        renderValue={isForKeywordDropdown || isTopicsDropDown ? () => value : null}
      >
        {showSearchBox ? (
          <ListSubheader className="dropdown-search-header">
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
            <div className="dropdown-search-box" {...stopMenuEvents}>
              <TextField
                // Focus belongs here the moment the menu opens: this input exists only
                // inside the open popup, so it never steals focus on page load.
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                fullWidth
                size="small"
                variant="outlined"
                value={searchQuery}
                placeholder={searchPlaceholder}
                inputProps={{ 'aria-label': searchPlaceholder, 'data-testid': 'dropdown-search-input' }}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
            </div>
          </ListSubheader>
        ) : null}
        {!hasSearchMatch ? (
          <MenuItem className="dropdown-menu dropdown-no-results" disabled>
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
            <div {...stopMenuEvents}>{noSearchResultsText}</div>
          </MenuItem>
        ) : null}
        {options &&
          options.map((option, index) => {
            let { colorClass } = getBarValue(option.score || 0);
            if (displayGroupName && option?.isGroupName) {
              return (
                <ListSubheader className={groupNameOptionClassName} value="disabled-group-name-qttr">
                  {option?.text}
                </ListSubheader>
              );
            }
            return (
              <MenuItem
                id={`${id}_menu_item`}
                className={`dropdown-menu ${option.hidden || !matchesSearch(option) ? 'd-none' : ''}`}
                disabled={option.disabled}
                key={option.text}
                value={option.value}
                style={groupNameOptionClassName ? { paddingLeft: '25px' } : {}}
              >
                {isForKeywordDropdown || (isTopicsDropDown && index != 0) ? (
                  <div id="content_recommendation__div__form_drop_down_6" className="keyword-list-wrapper">
                    <div id="content_recommendation__div__form_drop_down_7">
                      <div id="content_recommendation__div__form_drop_down_8" className="keyword" title={option.text}>
                        {option.text}
                        {option.isBranded ? <Star className="branded-star" /> : null}
                      </div>
                      {!option.isOverallEntry && (
                        <div className="competitor-count">{`${option.competitorCount} Competitors`}</div>
                      )}
                    </div>
                    {!option.isOverallEntry && (
                      <div>
                        <div id="content_recommendation__div__form_drop_down_9" className="score">
                          {option.score}
                        </div>
                        <div id="content_recommendation__div__form_drop_down_10">
                          <span id="content_recommendation__span__form_drop_down_11" className="keyword-topics-signal">
                            <LinearProgress
                              className={colorClass}
                              style={{ width: '43px', bottom: 0, left: 0, right: 0, top: 0, height: '5px' }}
                              variant="determinate"
                              value={option.score}
                            />
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ) : option?.displaySignalBar ? (
                  <p className="selected-menu">
                    <Signal value={option?.score || 0} />
                    <span>{option?.score || 0}</span>
                    {option?.deltaValue ? (
                      option?.deltaValue < 0 ? (
                        <span className="negative">
                          <ArrowDownward className="negative" /> {option?.deltaValue}
                        </span>
                      ) : (
                        <span className="positive">
                          <ArrowUpwardIcon className="positive" /> +{option?.deltaValue}
                        </span>
                      )
                    ) : null}
                    <span className="text" style={{}}>
                      {option.text}
                    </span>
                  </p>
                ) : (
                  option.text
                )}
                {option?.isLocked && !option?.hideLockIcon ? (
                  <div className="locked-icon-container">
                    <Tooltip
                      title={`This feature is not included in your ${
                        sessionUserType === USER_CLASSIFICATION.QWA_TOOLS
                          ? 'AI Tools plan'
                          : sessionUserType === USER_CLASSIFICATION.CONTENT_AI
                          ? 'Content AI plan'
                          : 'AI SEO Suite'
                      }`}
                    >
                      <svg
                        style={{ marginTop: '6px' }}
                        className={`MuiSvgIcon-root MuiSvgIcon-fontSizeMedium MuiBox-root css-1om0hkc lock-icon-header-selection ${classes.disabledMenu} `}
                        focusable="true"
                        aria-hidden="true"
                        viewBox="0 0 33 33"
                        data-testid="ContentPasteSearchOutlinedIcon"
                      >
                        <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6zm9 14H6V10h12v10zm-6-3c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z"></path>
                      </svg>
                    </Tooltip>
                  </div>
                ) : null}
              </MenuItem>
            );
          })}
      </Select>
    </FormControl>
  );
}
